// Synthesized arcade soundtrack for the auto-director / 24-7 stream.
// Same approach as audio.js: pure WebAudio, no assets. A small step-sequencer
// arranges a groove from a theme, and a dozen themes cycle so a stream running
// for hours never sits on one for long. Each theme picks its own instruments
// (square lead, synth strings, woodwind, brass, bells, organ, several basses),
// its own drum kit and its own rhythm feel (4/4, 3/4 waltz, swung 12/8,
// breakbeat, half-time, ambient), so they sound like different tracks rather
// than one engine in different keys. The arrangement thins and thickens with
// the director's action intensity: quiet skies get an ambient pad bed, a
// furball brings in drums and a busier lead.

const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);

// Scale = semitone offsets from the root. Degrees beyond the array wrap up an
// octave, so degree 9 on a 7-note scale is the third, an octave up.
const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  minorPent: [0, 3, 5, 7, 10],
  majorPent: [0, 2, 4, 7, 9],
  blues: [0, 3, 5, 6, 7, 10],
};

// Drum patterns: which steps each kit piece hits within a bar. Bars are
// stepsPerBar sixteenths long (16 = 4/4, 12 = 3/4). Pieces split into "core"
// (always on once drums kick in) and ornaments (hat/ride/shaker, added when
// busy) — see drumStep.
const DRUMS = {
  driving: { kick: [0, 6, 8, 14], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14] },
  fourFloor: { kick: [0, 4, 8, 12], clap: [4, 12], hat: [2, 6, 10, 14], shaker: [1, 3, 5, 7, 9, 11, 13, 15] },
  synthwave: { kick: [0, 4, 8, 12], snare: [4, 12], hat: [2, 6, 10, 14] },
  breakbeat: { kick: [0, 10], snare: [4, 12], hat: [0, 2, 3, 6, 8, 10, 11, 14] },
  dnb: { kick: [0, 6, 10], snare: [4, 12], hat: [0, 1, 2, 4, 5, 8, 9, 12, 13, 14] },
  halftime: { kick: [0], snare: [8], hat: [0, 4, 8, 12], tom: [14] },
  march: { kick: [0, 4, 8, 12], snare: [2, 6, 10, 14], rim: [0, 8] },
  boombap: { kick: [0, 7, 10], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14] },
  toms: { kick: [0, 8], snare: [4, 12], tom: [2, 6, 10, 14], shaker: [1, 5, 9, 13] },
  claps: { kick: [0, 4, 8, 12], clap: [4, 12], shaker: [1, 3, 5, 7, 9, 11, 13, 15] },
  ambient: { ride: [0], shaker: [8] },
  waltz: { kick: [0], snare: [4, 8], hat: [0, 2, 4, 6, 8, 10] }, // 12-step
  jazz12: { ride: [0, 4, 6, 10], kick: [0, 6], rim: [4, 10] }, // 12-step swung
  // Sousa-style band: a steady bass drum, snare on the backbeats with rolling
  // taps in between, and a crash on the phrase (every 4th bar — see drumStep).
  marchband: { bassdrum: [0, 4, 8, 12], snare: [4, 12], snaretap: [2, 6, 10, 14], crash: [0] },
};
const CORE_DRUMS = ["kick", "bassdrum", "snare", "crash", "tom", "clap", "rim"];

// The dozen themes. Each names a scale, root (MIDI), tempo, drum kit and a set
// of instruments per role, so they arrange very differently. prog = chord-root
// scale degrees, one per bar, looping. space = delay/reverb send (wetter for
// the ambient ones). drumGate = energy needed before drums come in (high for
// the ambient themes so calm skies stay drumless).
const THEMES = [
  { name: "Scramble", combat: true, scale: "minorPent", root: 45, bpm: 134, steps: 16, swing: 0, prog: [0, 0, 4, 3], drums: "driving", drumGate: 0.12, space: 0.12,
    bass: "driving8", bassVoice: "saw", pad: null, lead: "square", leadStyle: "arp", color: "bell" },
  { name: "Dawn Patrol", scale: "dorian", root: 43, bpm: 82, steps: 16, swing: 0, prog: [0, 5, 3, 4], drums: "ambient", drumGate: 0.8, space: 0.3,
    bass: "roots", bassVoice: "sub", pad: "strings", lead: "flute", leadStyle: "melody", color: "bell" },
  { name: "Ace High", scale: "major", root: 48, bpm: 120, steps: 16, swing: 0, prog: [0, 4, 5, 3], drums: "march", drumGate: 0.16, space: 0.18,
    bass: "roots", bassVoice: "saw", pad: "organ", lead: "brass", leadStyle: "melody", color: null },
  { name: "Flak Alley", combat: true, scale: "phrygian", root: 41, bpm: 110, steps: 16, swing: 0, prog: [0, 1, 0, 6], drums: "toms", drumGate: 0.3, space: 0.22,
    bass: "acid", bassVoice: "acid", pad: "strings", lead: "brass", leadStyle: "sparse", color: null },
  { name: "Tailspin", combat: true, scale: "harmonicMinor", root: 45, bpm: 166, steps: 16, swing: 0, prog: [0, 6, 4, 0], drums: "dnb", drumGate: 0.15, space: 0.12,
    bass: "driving8", bassVoice: "sub", pad: null, lead: "pluck", leadStyle: "arp", color: null },
  { name: "Sortie", scale: "mixolydian", root: 46, bpm: 116, steps: 16, swing: 0, prog: [0, 3, 4, 0], drums: "synthwave", drumGate: 0.15, space: 0.18,
    bass: "offbeat", bassVoice: "saw", pad: "pad", lead: "saw", leadStyle: "melody", color: "bell" },
  { name: "Vapor Trail", scale: "lydian", root: 50, bpm: 94, steps: 16, swing: 0, prog: [0, 4, 1, 5], drums: "ambient", drumGate: 0.85, space: 0.4,
    bass: "roots", bassVoice: "sub", pad: "pad", lead: "flute", leadStyle: "sparse", color: "bell" },
  { name: "Dogfight", combat: true, scale: "blues", root: 45, bpm: 150, steps: 16, swing: 0, prog: [0, 0, 3, 4], drums: "breakbeat", drumGate: 0.12, space: 0.12,
    bass: "driving8", bassVoice: "saw", pad: "organ", lead: "square", leadStyle: "melody", color: "organ" },
  { name: "Convoy", scale: "minor", root: 40, bpm: 104, steps: 12, swing: 0.32, prog: [0, 5, 6, 4], drums: "jazz12", drumGate: 0.2, space: 0.2,
    bass: "walking", bassVoice: "saw", pad: null, lead: "flute", leadStyle: "melody", color: "rim" },
  { name: "Victory Roll", scale: "majorPent", root: 48, bpm: 126, steps: 16, swing: 0, prog: [0, 3, 4, 4], drums: "fourFloor", drumGate: 0.15, space: 0.16,
    bass: "roots", bassVoice: "saw", pad: "organ", lead: "brass", leadStyle: "arp", color: "bell" },
  { name: "Night Sortie", scale: "phrygian", root: 38, bpm: 88, steps: 16, swing: 0, prog: [0, 6, 1, 0], drums: "halftime", drumGate: 0.4, space: 0.28,
    bass: "roots", bassVoice: "sub", pad: "strings", lead: "bell", leadStyle: "sparse", color: null },
  { name: "Bandits", combat: true, scale: "harmonicMinor", root: 43, bpm: 138, steps: 16, swing: 0, prog: [0, 4, 6, 5], drums: "claps", drumGate: 0.15, space: 0.12,
    bass: "acid", bassVoice: "acid", pad: null, lead: "square", leadStyle: "melody", color: "brass" },
  // A rousing Sousa march: tuba oom on the strong beats, brass "pah" chords on
  // the afterbeats, a brass melody up top, and a piccolo obbligato sparkling
  // over it when the action peaks (the trio descant). I-V-I-V, bright major.
  // A rousing Sousa march. Unlike the other themes it plays a *composed*
  // melody (leadStyle "phrase") rather than improvising — a march needs an
  // intentional, jubilant tune. melody = eight bars of eighth-notes as scale
  // degrees (0 = tonic, null = rest), played over an I/IV/V progression; tuba
  // oom on the strong beats, brass "pah" on the afterbeats, piccolo descant on
  // top when it swells. Bright major, brisk quick-march tempo.
  { name: "Stars & Stripes", scale: "major", root: 46, bpm: 130, steps: 16, swing: 0, drums: "marchband", drumGate: 0.14, space: 0.14,
    prog: [0, 0, 4, 0, 3, 0, 4, 0],
    melody: [
      [0, 2, 4, 7, 7, 4, 2, 4],
      [7, 4, 2, 0, 4, 4, 4, null],
      [4, 6, 8, 6, 4, 8, 6, 4],
      [7, 7, 4, 2, 0, 2, 4, null],
      [3, 5, 7, 5, 3, 5, 7, 9],
      [7, 4, 2, 0, 2, 4, 7, null],
      [4, 6, 8, 9, 8, 6, 4, 6],
      [7, 4, 0, 4, 7, 7, 0, null],
    ],
    bass: "march", bassVoice: "saw", pad: "afterbeat", lead: "brass", leadStyle: "phrase", leadOct: 1, color: "piccolo" },
  // Stripped-down 8-bit themes: bare square/triangle voices, a pulse bass, a
  // basic beat — no pads, reverb or extra layers. The simple arcade sound.
  { name: "Insert Coin", scale: "majorPent", root: 48, bpm: 152, steps: 16, swing: 0, prog: [0, 3, 4, 4], drums: "fourFloor", drumGate: 0.08, space: 0.04,
    bass: "driving8", bassVoice: "square", pad: null, lead: "chip", leadStyle: "arp", arp: [0, 2, 4, 2, 4, 2, 4, 7], color: null },
  { name: "Pixel Dash", combat: true, scale: "minorPent", root: 45, bpm: 164, steps: 16, swing: 0, prog: [0, 0, 4, 3], drums: "breakbeat", drumGate: 0.08, space: 0.05,
    bass: "driving8", bassVoice: "square", pad: null, lead: "chip", leadStyle: "arp", arp: [0, 3, 7, 3], arpRate: 1, color: null },
  { name: "Boss Fight", combat: true, scale: "phrygian", root: 41, bpm: 144, steps: 16, swing: 0, prog: [0, 1, 0, 6], drums: "driving", drumGate: 0.08, space: 0.05,
    bass: "driving8", bassVoice: "square", pad: null, lead: "tri", leadStyle: "arp", arp: [0, 2, 3, 2], color: null },
];
const THEME_3_4 = 12;

const THEME_SECS = 600; // cycle to the next theme roughly every ten minutes

export class GameMusic {
  // Shares the GameAudio context and master bus so mute and the audio graph
  // stay in one place. onTheme(name) is an optional notifier (HUD caption).
  constructor(audio, onTheme = null) {
    this.audio = audio;
    this.onTheme = onTheme;
    this.started = false;
    this.intensity = 0; // raw director score, set from outside
    this.energy = 0; // smoothed 0..1 arrangement drive
    this.themeIdx = 0;
    this.bar = 0;
    this.step = 0;
    this.lastLead = 0; // last lead scale-degree, for stepwise melodies
    this.nextNoteTime = 0;
    this.themeStartedAt = 0;
    this.timer = null;
  }

  /** Build the music bus and kick off the scheduler. Needs audio.ensure() first. */
  start() {
    if (this.started || !this.audio.ctx) return;
    const ctx = this.audio.ctx;
    this.ctx = ctx;
    this.started = true;
    if (this.musicGain) {
      // Resuming after stop(): the bus is already built — just restart the
      // scheduler clocks instead of stacking a second node graph.
      this.nextNoteTime = ctx.currentTime + 0.1;
      this.themeStartedAt = ctx.currentTime;
      this.step = 0;
      this.timer = setInterval(() => this.schedule(), 25);
      this.onTheme?.(this.theme().name);
      return;
    }

    // A compressor glues the layers and stops stacked notes from clipping over
    // a long unattended run. The whole bus hangs off master, so toggleMute()
    // silences the soundtrack along with the SFX, for free.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.ratio.value = 5;
    comp.attack.value = 0.005;
    comp.release.value = 0.25;
    comp.connect(this.audio.master);

    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = 0.0001;
    this.musicGain.connect(comp);

    // Per-role sub-buses, so each layer balances independently.
    this.leadGain = this.bus(0.85);
    this.padGain = this.bus(0.5);
    this.bassGain = this.bus(0.95);
    this.drumGain = this.bus(1.0);
    this.colorGain = this.bus(0.6);

    // A feedback delay gives leads/pads/bells some space; the per-theme `space`
    // send sets how wet (ambient themes much wetter than the chase tracks).
    this.spaceSend = ctx.createGain();
    this.spaceSend.gain.value = 0.15;
    const delay = ctx.createDelay(1);
    delay.delayTime.value = 0.26;
    const fb = ctx.createGain();
    fb.gain.value = 0.34;
    const damp = ctx.createBiquadFilter();
    damp.type = "lowpass";
    damp.frequency.value = 2600;
    this.spaceSend.connect(delay);
    delay.connect(damp);
    damp.connect(fb);
    fb.connect(delay);
    damp.connect(this.musicGain);

    // One shared noise buffer for drums and breath.
    const len = ctx.sampleRate * 1;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const nd = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) nd[i] = Math.random() * 2 - 1;

    this.nextNoteTime = ctx.currentTime + 0.1;
    this.themeStartedAt = ctx.currentTime;
    this.applyTheme();
    this.onTheme?.(this.theme().name);
    // 25 ms lookahead scheduler — the standard WebAudio pattern, immune to
    // timer jitter because notes are queued ahead against ctx.currentTime.
    this.timer = setInterval(() => this.schedule(), 25);
  }

  bus(gain) {
    const g = this.ctx.createGain();
    g.gain.value = gain;
    g.connect(this.musicGain);
    return g;
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.started = false;
    if (this.musicGain && this.ctx)
      this.musicGain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.2);
  }

  theme() {
    return THEMES[this.themeIdx];
  }

  applyTheme() {
    this.spaceSend.gain.setTargetAtTime(this.theme().space, this.ctx.currentTime, 0.2);
  }

  /**
   * Ask for a combat or calm theme ("combat"/"calm"). If the current theme
   * already matches, this clears any pending request. Otherwise the current
   * theme fades over ~2 s and swaps at the next bar boundary, at most once
   * per 60 s so a strafing pass can't ping-pong the soundtrack. Returns true
   * when a switch was newly queued. The 10-minute rotation is untouched.
   */
  requestThemeClass(cls) {
    if (!this.started || !this.ctx) return false;
    const want = cls === "combat";
    if (!!this.theme().combat === want) {
      this._pendingClass = null;
      this._fadeFrom = null;
      return false;
    }
    if (this._pendingClass === cls) return false;
    if (this.ctx.currentTime - (this._classSwitchAt ?? -Infinity) < 60) return false;
    this._pendingClass = cls;
    this._fadeFrom = this.ctx.currentTime;
    return true;
  }

  /** Director feeds its best-vantage score here; we map it to 0..1 energy. */
  setIntensity(score) {
    this.intensity = Math.max(0, Number(score) || 0);
  }

  /** A stinger on a kill cut — a bright flourish in the theme's lead voice. */
  accent() {
    if (!this.started || !this.ctx) return;
    const t = this.ctx.currentTime;
    const th = this.theme();
    const deg = th.prog[this.bar % th.prog.length];
    const voice = th.color === "brass" || th.lead === "brass" ? "brass" : "bell";
    [0, 2, 4].forEach((d, i) => this.leadVoice(voice, this.degFreq(th.root + 12, deg + d, 1), t + i * 0.05, 0.3));
  }

  // ---- pitch helpers ----------------------------------------------------

  // Scale-degree -> frequency. degree wraps across octaves; octave shifts up.
  degFreq(rootMidi, degree, octaveShift = 0) {
    const scale = SCALES[this.theme().scale];
    const len = scale.length;
    const oct = octaveShift + Math.floor(degree / len);
    const idx = ((degree % len) + len) % len;
    return midiToFreq(rootMidi + scale[idx] + 12 * oct);
  }

  // ---- low-level voice --------------------------------------------------

  // One ADSR oscillator with optional lowpass (static or swept), vibrato and a
  // wet send. Composite instruments below stack a few of these.
  oscNote(freq, t, dur, o) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = o.wave || "sawtooth";
    osc.frequency.value = freq;
    if (o.detune) osc.detune.value = o.detune;
    let node = osc;
    if (o.cutoff != null) {
      const f = ctx.createBiquadFilter();
      f.type = "lowpass";
      f.Q.value = o.q ?? 1;
      if (o.cutoffEnv) {
        f.frequency.setValueAtTime(o.cutoffEnv[0], t);
        f.frequency.linearRampToValueAtTime(o.cutoffEnv[1], t + (o.a || 0.01) + (o.d || 0.05));
        f.frequency.linearRampToValueAtTime(o.cutoff, t + dur);
      } else {
        f.frequency.value = o.cutoff;
      }
      osc.connect(f);
      node = f;
    }
    const g = ctx.createGain();
    const peak = o.peak ?? 0.2;
    const a = o.a ?? 0.005, d = o.d ?? 0.05, s = o.s ?? 0.7, r = o.r ?? 0.08;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + a);
    g.gain.linearRampToValueAtTime(Math.max(0.0001, peak * s), t + a + d);
    g.gain.setValueAtTime(Math.max(0.0001, peak * s), t + dur);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + r);
    node.connect(g);
    g.connect(o.dest || this.musicGain);
    if (o.wet) g.connect(this.spaceSend);
    if (o.vib) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = o.vib.rate;
      const lg = ctx.createGain();
      lg.gain.value = o.vib.depth;
      lfo.connect(lg);
      lg.connect(osc.detune);
      lfo.start(t);
      lfo.stop(t + dur + r + 0.05);
    }
    osc.start(t);
    osc.stop(t + dur + r + 0.05);
  }

  // ---- instruments ------------------------------------------------------

  pad(freq, t, dur, vol) {
    [-8, 0, 8].forEach((dt) =>
      this.oscNote(freq, t, dur, { wave: "sawtooth", detune: dt, dest: this.padGain, a: 0.35, d: 0.2, s: 0.85, r: 0.5, peak: vol * 0.5, cutoff: 1500, q: 0.7, wet: true }),
    );
  }

  strings(freq, t, dur, vol) {
    [-11, -4, 4, 11].forEach((dt) =>
      this.oscNote(freq, t, dur, { wave: "sawtooth", detune: dt, dest: this.padGain, a: 0.2, d: 0.15, s: 0.9, r: 0.45, peak: vol * 0.4, cutoff: 2600, q: 0.6, vib: { rate: 5, depth: 7 }, wet: true }),
    );
  }

  organ(freq, t, dur, vol, dest) {
    [1, 2, 3, 4].forEach((m, i) =>
      this.oscNote(freq * m, t, dur, { wave: "sine", dest: dest || this.padGain, a: 0.02, d: 0.03, s: 0.9, r: 0.07, peak: vol * [0.5, 0.28, 0.16, 0.1][i] }),
    );
  }

  flute(freq, t, dur, vol, dest) {
    this.oscNote(freq, t, dur, { wave: "triangle", dest: dest || this.leadGain, a: 0.06, d: 0.05, s: 0.9, r: 0.14, peak: vol * 0.85, vib: { rate: 5.5, depth: 13 }, wet: true });
    this.oscNote(freq * 2, t, dur, { wave: "sine", dest: dest || this.leadGain, a: 0.07, d: 0.05, s: 0.4, r: 0.1, peak: vol * 0.18, wet: true });
    // A whisper of breath noise gives it a woodwind edge.
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = freq * 2.2;
    f.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol * 0.06, t + 0.06);
    g.gain.setValueAtTime(vol * 0.06, t + dur);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.12);
    src.connect(f);
    f.connect(g);
    g.connect(dest || this.leadGain);
    src.start(t);
    src.stop(t + dur + 0.2);
  }

  // Piercing high woodwind for the march descant — thin, bright, fast vibrato.
  piccolo(freq, t, dur, vol, dest) {
    this.oscNote(freq, t, dur, { wave: "triangle", dest: dest || this.colorGain, a: 0.008, d: 0.03, s: 0.85, r: 0.06, peak: vol * 0.75, vib: { rate: 6.5, depth: 11 }, wet: true });
    this.oscNote(freq, t, dur, { wave: "square", dest: dest || this.colorGain, a: 0.008, d: 0.03, s: 0.35, r: 0.05, peak: vol * 0.14, cutoff: 8000, wet: true });
  }

  brass(freq, t, dur, vol, dest) {
    [-6, 6].forEach((dt) =>
      this.oscNote(freq, t, dur, { wave: "sawtooth", detune: dt, dest: dest || this.leadGain, a: 0.04, d: 0.1, s: 0.78, r: 0.12, peak: vol * 0.5, cutoff: 3800, cutoffEnv: [500, 4200], q: 1, vib: { rate: 5, depth: 5 }, wet: true }),
    );
  }

  bell(freq, t, vol, dest) {
    const ctx = this.ctx;
    [1, 2.01, 3.0].forEach((m, i) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = freq * m;
      const g = ctx.createGain();
      const peak = vol * [1, 0.5, 0.25][i];
      g.gain.setValueAtTime(peak, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      o.connect(g);
      g.connect(dest || this.leadGain);
      g.connect(this.spaceSend);
      o.start(t);
      o.stop(t + 1.2);
    });
  }

  pluck(freq, t, vol, wave = "square") {
    this.oscNote(freq, t, 0.12, { wave, dest: this.leadGain, a: 0.003, d: 0.08, s: 0.2, r: 0.06, peak: vol, cutoff: 4200, wet: true });
  }

  // Bare 8-bit lead: a single raw oscillator with a simple envelope — no
  // filter, vibrato or reverb. The stripped-down sound of the chiptune themes.
  chip(freq, t, dur, vol, wave = "square") {
    this.oscNote(freq, t, dur, { wave, dest: this.leadGain, a: 0.002, d: 0.04, s: 0.6, r: 0.05, peak: vol });
  }

  synthLead(freq, t, dur, vol, wave) {
    this.oscNote(freq, t, dur, { wave, dest: this.leadGain, a: 0.01, d: 0.06, s: 0.7, r: 0.1, peak: vol, cutoff: 2000 + this.energy * 4500, q: 1.2, vib: { rate: 5, depth: 5 }, wet: true });
  }

  // Dispatch a lead note to the theme's chosen voice.
  leadVoice(name, freq, t, dur, vol = 0.2) {
    if (name === "pluck") this.pluck(freq, t, vol);
    else if (name === "flute") this.flute(freq, t, dur, vol);
    else if (name === "brass") this.brass(freq, t, dur, vol);
    else if (name === "bell") this.bell(freq, t, vol);
    else if (name === "organ") this.organ(freq, t, dur, vol, this.leadGain);
    else if (name === "chip") this.chip(freq, t, dur, vol, "square");
    else if (name === "tri") this.chip(freq, t, dur, vol, "triangle");
    else this.synthLead(freq, t, dur, vol, name === "saw" ? "sawtooth" : "square");
  }

  bassVoice(name, freq, t, dur, vol) {
    if (name === "sub") this.oscNote(freq, t, dur, { wave: "sine", dest: this.bassGain, a: 0.005, d: 0.05, s: 0.9, r: 0.08, peak: vol });
    else if (name === "acid") this.oscNote(freq, t, dur, { wave: "sawtooth", dest: this.bassGain, a: 0.005, d: 0.12, s: 0.3, r: 0.06, peak: vol, cutoff: 1800, cutoffEnv: [180, 1800], q: 9 });
    else if (name === "square") this.oscNote(freq, t, dur, { wave: "square", dest: this.bassGain, a: 0.004, d: 0.06, s: 0.6, r: 0.06, peak: vol }); // bare pulse bass
    else this.oscNote(freq, t, dur, { wave: "sawtooth", dest: this.bassGain, a: 0.005, d: 0.08, s: 0.7, r: 0.08, peak: vol, cutoff: 900, q: 1.2 });
  }

  // ---- drum kit ---------------------------------------------------------

  hit(piece, t) {
    if (piece === "kick") return this.kick(t);
    if (piece === "bassdrum") return this.bassdrum(t);
    if (piece === "snare") return this.snare(t);
    if (piece === "snaretap") return this.noiseHit(t, { type: "bandpass", freq: 1900, q: 0.9, dur: 0.05, vol: 0.14 });
    if (piece === "crash") return this.crash(t);
    if (piece === "hat") return this.hat(t, this.energy > 0.7);
    if (piece === "tom") return this.tom(t, 170);
    if (piece === "clap") return this.clap(t);
    if (piece === "rim") return this.rim(t);
    if (piece === "ride") return this.ride(t);
    if (piece === "shaker") return this.shaker(t);
  }

  bassdrum(t) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(90, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.14);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.55, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g);
    g.connect(this.drumGain);
    o.start(t);
    o.stop(t + 0.22);
  }

  crash(t) {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 5000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.22, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    src.connect(f);
    f.connect(g);
    g.connect(this.drumGain);
    g.connect(this.spaceSend);
    src.start(t);
    src.stop(t + 1.0);
  }

  kick(t) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(135, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    osc.connect(g);
    g.connect(this.drumGain);
    osc.start(t);
    osc.stop(t + 0.18);
  }

  noiseHit(t, { type, freq, q = 1, dur, vol }) {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.drumGain);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  snare(t) {
    this.noiseHit(t, { type: "bandpass", freq: 1800, q: 0.8, dur: 0.14, vol: 0.35 });
    this.oscNote(190, t, 0.06, { wave: "triangle", dest: this.drumGain, a: 0.001, d: 0.05, s: 0.1, r: 0.02, peak: 0.12 });
  }

  hat(t, open = false) {
    this.noiseHit(t, { type: "highpass", freq: 7000, q: 1, dur: open ? 0.09 : 0.03, vol: open ? 0.1 : 0.12 });
  }

  tom(t, freq) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.6, t + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.4, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g);
    g.connect(this.drumGain);
    o.start(t);
    o.stop(t + 0.24);
  }

  clap(t) {
    [0, 0.012, 0.026, 0.05].forEach((off, i) =>
      this.noiseHit(t + off, { type: "bandpass", freq: 1500, q: 1.2, dur: i === 3 ? 0.12 : 0.03, vol: i === 3 ? 0.16 : 0.26 }),
    );
  }

  rim(t) {
    this.noiseHit(t, { type: "bandpass", freq: 2500, q: 3, dur: 0.04, vol: 0.22 });
  }

  ride(t) {
    this.noiseHit(t, { type: "highpass", freq: 6000, q: 1, dur: 0.3, vol: 0.07 });
    this.oscNote(5200, t, 0.22, { wave: "square", dest: this.drumGain, a: 0.001, d: 0.2, s: 0.1, r: 0.04, peak: 0.015 });
  }

  shaker(t) {
    this.noiseHit(t, { type: "highpass", freq: 8000, q: 1, dur: 0.04, vol: 0.05 });
  }

  // ---- arrangement ------------------------------------------------------

  schedule() {
    const ctx = this.ctx;
    if (!ctx) return;

    // Smooth the energy toward the (normalised) director intensity so the
    // arrangement breathes instead of snapping. ~0..1 over the useful range.
    const target = Math.min(1, this.intensity / 4);
    this.energy += (target - this.energy) * 0.05;

    // Fade out over the last two seconds of a theme so the swap is a clean
    // cross, not a hard cut. Overall level also lifts a touch with energy.
    const elapsed = ctx.currentTime - this.themeStartedAt;
    const left = THEME_SECS - elapsed;
    let fade = left < 2 ? Math.max(0.0001, left / 2) : 1;
    // A requested combat/calm handoff borrows the same 2 s crossfade.
    if (this._pendingClass)
      fade = Math.min(fade, Math.max(0.0001, 1 - (ctx.currentTime - this._fadeFrom) / 2));
    this.musicGain.gain.setTargetAtTime((0.42 + this.energy * 0.13) * fade, ctx.currentTime, 0.1);

    const th = this.theme();
    const sixteenth = 60 / th.bpm / 4;
    this.barDur = th.steps * sixteenth;
    while (this.nextNoteTime < ctx.currentTime + 0.2) {
      // Swing: nudge the off-16ths later for a shuffled feel.
      const swung = this.step % 2 === 1 ? th.swing * sixteenth * 0.6 : 0;
      this.playStep(this.step, this.nextNoteTime + swung);
      this.step++;
      if (this.step >= th.steps) {
        this.step = 0;
        this.bar++;
      }
      this.nextNoteTime += sixteenth;
    }

    if (this._pendingClass && ctx.currentTime - this._fadeFrom >= 2 && this.step === 0) {
      const want = this._pendingClass === "combat";
      const picks = [];
      for (let i = 0; i < THEMES.length; i++)
        if (!!THEMES[i].combat === want && i !== this.themeIdx) picks.push(i);
      if (picks.length) this.themeIdx = picks[Math.floor(Math.random() * picks.length)];
      this._pendingClass = null;
      this._fadeFrom = null;
      this._classSwitchAt = ctx.currentTime;
      this.bar = 0;
      this.lastLead = 0;
      this.themeStartedAt = ctx.currentTime;
      this.applyTheme();
      this.onTheme?.(this.theme().name);
    } else if (elapsed >= THEME_SECS && this.step === 0) {
      this.themeIdx = (this.themeIdx + 1) % THEMES.length;
      this.bar = 0;
      this.lastLead = 0;
      this.themeStartedAt = ctx.currentTime;
      this.applyTheme();
      this.onTheme?.(this.theme().name);
    }
  }

  playStep(step, t) {
    const th = this.theme();
    const chordDeg = th.prog[this.bar % th.prog.length];

    if (step === 0 && th.pad && th.pad !== "afterbeat") this.padChord(th, chordDeg, t);
    if (th.pad === "afterbeat") this.afterbeat(th, step, chordDeg, t);
    this.bassStep(th, step, chordDeg, t);
    if (this.energy > th.drumGate) this.drumStep(th.drums, step, t, this.energy > th.drumGate + 0.2);
    this.leadStep(th, step, chordDeg, t);
    if (th.color) this.colorStep(th, step, chordDeg, t);
  }

  padChord(th, chordDeg, t) {
    const vol = 0.13 * (0.65 + this.energy * 0.45);
    const tones = this.energy > 0.55 ? [0, 2, 4, 6] : [0, 2, 4];
    const dur = this.barDur * 0.96;
    for (const d of tones) {
      const f = this.degFreq(th.root + 12, chordDeg + d, 0);
      if (th.pad === "strings") this.strings(f, t, dur, vol);
      else if (th.pad === "organ") this.organ(f, t, dur, vol * 1.05);
      else this.pad(f, t, dur, vol);
    }
  }

  // March "pah": short brass chords on the backbeats answering the tuba's oom.
  afterbeat(th, step, chordDeg, t) {
    if (step !== 4 && step !== 12) return;
    const vol = 0.1 * (0.7 + this.energy * 0.4);
    // Mid-register horn stabs, sitting between the tuba and the melody.
    [0, 2, 4].forEach((d) => this.brass(this.degFreq(th.root, chordDeg + d, 0), t, 0.14, vol, this.padGain));
  }

  bassStep(th, step, chordDeg, t) {
    const half = Math.floor(th.steps / 2);
    const play = (deg, oct, dur, vol) => this.bassVoice(th.bassVoice, this.degFreq(th.root, chordDeg + deg, oct), t, dur, vol);
    switch (th.bass) {
      case "roots":
        if (step === 0) play(0, 0, this.barDur * 0.5, 0.32);
        else if (step === half && this.energy > 0.25) play(4, 0, this.barDur * 0.3, 0.24);
        break;
      case "driving8":
        if (step % 2 === 0) play(0, 0, 0.13, 0.26);
        break;
      case "offbeat":
        if (step === 0 || step % 4 === 2) play(0, 0, 0.17, 0.26);
        break;
      case "walking": {
        if (step % 4 === 0) {
          const walk = [0, 1, 2, 4];
          play(walk[(step / 4) % 4], 0, this.barDur / 4 * 0.9, 0.26);
        }
        break;
      }
      case "acid": {
        const steps = [0, 3, 6, 8, 11, 14];
        const i = steps.indexOf(step);
        if (i >= 0) play(0, [0, 0, 1, 0, 1, 0][i], 0.14, 0.24);
        break;
      }
      case "march": {
        // Tuba oom: tonic on beat 1, dominant (the fifth) on beat 3, low and
        // detached.
        const q = this.barDur / 4 * 0.8;
        if (step === 0) play(0, -1, q, 0.34);
        else if (step === 8) play(4, -1, q, 0.32);
        break;
      }
    }
  }

  drumStep(name, step, t, ornaments) {
    const pat = DRUMS[name];
    for (const piece in pat) {
      if (!pat[piece].includes(step)) continue;
      if (!ornaments && !CORE_DRUMS.includes(piece)) continue;
      if (piece === "crash" && this.bar % 4 !== 0) continue; // crash on the phrase, not every bar
      this.hit(piece, t);
    }
  }

  leadStep(th, step, chordDeg, t) {
    const e = this.energy;
    const oct = th.leadOct ?? 1;
    if (th.leadStyle === "phrase") {
      // Composed tune: one eighth-note per two steps, always playing so the
      // march carries its melody even in calm stretches.
      if (step % 2 !== 0) return;
      const bar = th.melody[this.bar % th.melody.length];
      const d = bar[step / 2];
      if (d == null) return;
      this.leadVoice(th.lead, this.degFreq(th.root, d, oct), t, 0.16, 0.26);
      return;
    }
    if (th.leadStyle === "arp") {
      const rate = th.arpRate ?? 2;
      if (step % rate !== 0) return;
      if (e < 0.18 && step % 4 !== 0) return;
      const fig = th.arp ?? [0, 2, 4, 7, 4, 2];
      const deg = chordDeg + fig[(step / rate) % fig.length];
      this.leadVoice(th.lead, this.degFreq(th.root, deg, oct), t, 0.16, 0.2);
      return;
    }
    if (th.leadStyle === "sparse") {
      // Long held notes over the pad; mostly chord tones.
      if (step !== 0 && step !== Math.floor(th.steps / 2)) return;
      if (step !== 0 && e < 0.3) return;
      const tones = [4, 2, 6, 0];
      const deg = chordDeg + tones[(this.bar + (step ? 1 : 0)) % tones.length];
      this.leadVoice(th.lead, this.degFreq(th.root, deg, oct), t, this.barDur * 0.45, 0.22);
      return;
    }
    // "melody": stepwise lines that land on chord tones on the beat, with
    // passing tones in between — sounds composed, not random.
    const onBeat = step % 4 === 0;
    const eligible = onBeat || (step % 2 === 0 && e > 0.4) || e > 0.72;
    if (!eligible) return;
    const prob = onBeat ? 0.55 + 0.4 * e : 0.45 * e;
    if (Math.random() > prob) return;
    let deg;
    if (onBeat) {
      const tones = [chordDeg, chordDeg + 2, chordDeg + 4];
      deg = tones[Math.floor(Math.random() * tones.length)];
    } else {
      deg = this.lastLead + (Math.random() < 0.5 ? 1 : -1);
    }
    this.lastLead = deg;
    this.leadVoice(th.lead, this.degFreq(th.root, deg, oct), t, onBeat ? 0.26 : 0.15, 0.2);
  }

  colorStep(th, step, chordDeg, t) {
    const e = this.energy;
    if (th.color === "bell") {
      if (e > 0.5 && (step === 6 || step === 14) && Math.random() < 0.6) {
        this.bell(this.degFreq(th.root + 12, chordDeg + (Math.random() < 0.5 ? 4 : 7), 1), t, 0.12, this.colorGain);
      }
    } else if (th.color === "organ") {
      // Off-beat stabs.
      if (e > 0.4 && (step === 2 || step === 10)) {
        [0, 2, 4].forEach((d) => this.organ(this.degFreq(th.root + 12, chordDeg + d, 0), t, 0.12, 0.06, this.colorGain));
      }
    } else if (th.color === "brass") {
      if (e > 0.5 && step === 8) {
        [0, 4].forEach((d) => this.brass(this.degFreq(th.root + 12, chordDeg + d, 0), t, 0.18, 0.14, this.colorGain));
      }
    } else if (th.color === "rim") {
      if (step % 3 === 0 && e > 0.25) this.rim(t);
    } else if (th.color === "piccolo") {
      // The trio descant: a bright running obbligato two octaves up. Eighths as
      // the march builds, breaking into showy sixteenths when it really swells.
      if (e <= 0.42) return;
      const six = e > 0.62;
      if (!six && step % 2 !== 0) return;
      const fig8 = [7, 11, 9, 12, 11, 9, 12, 14];
      const fig16 = [7, 9, 11, 12, 11, 9, 11, 12, 14, 12, 11, 9, 7, 9, 11, 12];
      const deg = six ? fig16[step] : fig8[step / 2];
      this.piccolo(this.degFreq(th.root, deg, 2), t, six ? 0.1 : 0.14, 0.12);
    }
  }
}
